import { Component } from '@theme/component';
import { SearchspringTrackingService } from "@theme/searchspring-tracking-service";

export default class SearchspringContentListing extends Component {
  constructor() {
    super();
    this.contentSiteId = null;
    this.currentSearchQuery = '';
    this.currentPage = 1;
    this.resultsPerPage = 20;
    this.currentSort = '';
    this.currentResponseId = null;
    this.sortingComponent = null;
    this.sortingContainer = null;
    this.sortingTemplate = null;
    this.searchspringTrackingService = null;
    this.pageLoadId = crypto.randomUUID?.() || Math.random().toString(36).substring(2);
    this.trackedImpressions = new Set();
    this.impressionObserver = null;
  }

  async connectedCallback() {
    super.connectedCallback();

    this.contentSiteId = this.dataset.contentSiteId;
    if (!this.contentSiteId) return;

    this.searchspringTrackingService = new SearchspringTrackingService({ siteId: this.contentSiteId });

    this.articlesContainer = this.querySelector('.article-grid');
    this.loadMoreBtn = this.querySelector('.js-articles-load-more');

    if (!this.articlesContainer) {
      return;
    }

    // Cache sorting component and template
    this.sortingComponent = document.querySelector('ss-sorting-articles-component');
    this.sortingTemplate = document.getElementById('sorting-articles-option-template');
    this.contentTemplate = document.getElementById('content-card-template');

    this.currentSearchQuery = new URLSearchParams(window.location.search).get('q') || '';

    // Use sort_content_by parameter to avoid affecting product sorting
    this.currentSort = new URLSearchParams(window.location.search).get('sort_content_by') || '';

    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', () => this.loadNextPage());
    }

    await this.initialFetch();
  }

  disconnectedCallback() {
    if (this.impressionObserver) {
      this.impressionObserver.disconnect();
    }
  }

  getSortingContainer() {
    if (!this.sortingContainer && this.sortingComponent) {
      this.sortingContainer = this.sortingComponent.querySelector('.js-sorting-articles-options-container');
    }
    return this.sortingContainer;
  }

  buildApiUrl(page = 1) {
    const params = new URLSearchParams({
      siteId: this.contentSiteId,
      q: this.currentSearchQuery,
      page: page,
      resultsPerPage: this.resultsPerPage,
      resultsFormat: 'json',
      beacon: 'true',
      test: 'true'
    });

    if (this.currentSort && this.currentSort.includes(':')) {
      const [field, direction] = this.currentSort.split(':');
      params.set(`sort.${field}`, direction);
    }

    return `https://${this.contentSiteId}.a.searchspring.io/api/search/search.json?${params}`;
  }

  async fetchArticles(page = 1) {
    const url = this.buildApiUrl(page);

    try {
      this.classList.add('is-updating');

      const response = await fetch(url);
      if (!response.ok) throw new Error(`API Error: ${response.status}`);

      const data = await response.json();

      if (data?.responseId) {
        this.currentResponseId = data.responseId;
      }

      return data;
    } catch (error) {
      return null;
    } finally {
      this.classList.remove('is-updating');
    }
  }

  async initialFetch() {
    const data = await this.fetchArticles(this.currentPage);

    if (data) {
      this.renderArticles(data, false);
      this.#trackContentView(data);
      this.#sendBeaconEvent('render', { responseId: this.currentResponseId });

      // Wait for ss-sorting-articles-component to initialize
      requestAnimationFrame(() => {
        this.renderSorting(data);
      });
    }
  }

  async loadNextPage() {
    if (this.loadMoreBtn?.classList.contains('is-loading')) return;

    this.currentPage++;
    this.loadMoreBtn?.classList.add('is-loading');

    const data = await this.fetchArticles(this.currentPage);

    if (data) {
      this.renderArticles(data, true);
    } else {
      this.currentPage--;
    }

    this.loadMoreBtn?.classList.remove('is-loading');
  }

  renderArticles(data, append = false) {
    const articles = data.results || [];

    if (!append) {
      this.articlesContainer.innerHTML = '';
      this.trackedImpressions.clear();
    }

    articles.forEach(article => {
      this.articlesContainer.appendChild(this.renderContent(article));
    });

    // Update all counters at once
    this.updateResultsCount(data.pagination);

    if (this.loadMoreBtn) {
      const hasMore = data.pagination && data.pagination.currentPage < data.pagination.totalPages;
      this.loadMoreBtn.classList.toggle('hidden', !hasMore);
    }

    this.#setupImpressionTracking();
  }

  renderContent(article) {
    if (!this.contentTemplate) {
      return document.createElement('div');
    }

    const clone = this.contentTemplate.content.cloneNode(true);

    const url = article.url || '#';
    const title = article.title || article.name || '';
    const excerpt = article.excerpt || article.description || '';
    const image = article.image || article.thumbnailImageUrl;
    const uid = article.uid;

    // Set data attributes for tracking
    const listItem = clone.querySelector('li');
    if (listItem && uid) {
      listItem.setAttribute('data-content-uid', uid);
      listItem.setAttribute('data-impression-key', uid);
    }

    clone.querySelectorAll('.js-content-url').forEach(el => {
      el.href = url;
      if (uid) {
        el.addEventListener('click', () => this.#handleContentClick(uid), { once: true });
      }
    });
    clone.querySelector('.js-content-title').textContent = title;
    clone.querySelector('.js-content-excerpt').textContent = this.truncateText(excerpt, 15);

    const img = clone.querySelector('.js-content-image');
	const placeholderImage = img.getAttribute('data-placeholder-src');
    img.src = image || placeholderImage;
    img.alt = title;

    return clone;
  }

  truncateText(text, wordCount) {
    if (!text) return '';
    const words = text.split(/\s+/);
    if (words.length <= wordCount) return text;
    return words.slice(0, wordCount).join(' ') + '...';
  }

  updateResultsCount(pagination) {
    if (!pagination) return;

    const totalResults = pagination.totalResults || 0;
    const visibleResults = pagination.end || 0;

    // Update all count elements in one pass
    const updates = [
      { selector: '.js-search-articles-count', value: totalResults },
      { selector: '.js-visible-content-results', value: visibleResults },
      { selector: '.js-total-content-results', value: totalResults }
    ];

    updates.forEach(({ selector, value }) => {
      document.querySelectorAll(selector).forEach(el => {
        if (el) el.textContent = value;
      });
    });
  }

  renderSorting(data) {
    const sortingContainer = this.getSortingContainer();
    if (!sortingContainer) {
      return;
    }

    if (!data.sorting?.options) {
      return;
    }

    if (!this.sortingTemplate) {
      return;
    }

    // Clear existing options and use event delegation
    sortingContainer.innerHTML = '';

    // Create document fragment for better performance
    const fragment = document.createDocumentFragment();

    data.sorting.options.forEach((option) => {
      const clone = this.sortingTemplate.content.cloneNode(true);
      const optionDiv = clone.querySelector('.sorting-filter__option');
      const input = clone.querySelector('input');
      const label = clone.querySelector('.sorting-filter__label');

      if (!input || !label || !optionDiv) return;

      const sortValue = `${option.field}:${option.direction}`;
      const isSelected = sortValue === this.currentSort;

      input.value = sortValue;
      input.name = 'sort_content_by';
      input.checked = isSelected;
      input.dataset.optionName = option.label;

      // Set aria attributes for accessibility
      optionDiv.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      optionDiv.setAttribute('tabindex', isSelected ? '0' : '-1');

      label.textContent = option.label;

      fragment.appendChild(clone);
    });

    sortingContainer.appendChild(fragment);

    // Use event delegation instead of individual listeners
    if (sortingContainer instanceof HTMLElement && !sortingContainer.dataset.listenerAttached) {
      sortingContainer.addEventListener('change', (e) => {
        const target = e.target;
        if (target instanceof HTMLInputElement && target.type === 'radio' && target.name === 'sort_content_by') {
          this.handleSortChange(target.value);
        }
      });
      sortingContainer.dataset.listenerAttached = 'true';
    }

    // Update sorting status text
    this.updateSortingStatus();
  }

  updateSortingStatus() {
    if (!this.sortingComponent) return;

    const statusElement = this.sortingComponent.querySelector('.js-sorting-article-status');
    if (!statusElement) return;

    const sortingContainer = this.getSortingContainer();
    let selectedInput = sortingContainer?.querySelector('input[type="radio"]:checked');

    // If no option is selected, use the first one as default
    if (!selectedInput) {
      selectedInput = sortingContainer?.querySelector('input[type="radio"]');
    }

    if (selectedInput instanceof HTMLInputElement && selectedInput.dataset.optionName) {
      statusElement.textContent = selectedInput.dataset.optionName;
    }
  }

  async handleSortChange(sortValue) {
    this.currentSort = sortValue;

    // Use sort_content_by parameter to avoid affecting product sorting (sort_by)
    const url = new URL(window.location.href);
    url.searchParams.set('sort_content_by', sortValue);

    // Remove page parameter when sorting changes
    url.searchParams.delete('page');

    // Convert URL object to string for pushState
    window.history.pushState({}, '', url.toString());

    this.currentPage = 1;
    const data = await this.fetchArticles(this.currentPage);
    if (data) {
      this.renderArticles(data, false);
      this.updateSortingStatus();
      this.#sendBeaconEvent('render', { responseId: this.currentResponseId });
    }
  }

  #sendBeaconEvent(event, data, keepAlive = false) {
    if (!this.contentSiteId || !this.searchspringTrackingService) return;

    this.searchspringTrackingService.sendEvent({
      siteId: this.contentSiteId,
      event,
      data,
      keepAlive,
      pageUrl: window.location.href,
      userId: this.#getOrSetCookie('ssUserId'),
      sessionId: this.#getOrSetCookie('ssSessionId'),
      pageLoadId: this.pageLoadId
    });
  }

  #setupImpressionTracking() {
    if (!this.currentResponseId || !this.articlesContainer) return;

    if (this.impressionObserver) this.impressionObserver.disconnect();

    const pendingResults = [];
    let flushScheduled = false;

    const flush = () => {
      if (pendingResults.length === 0 || !this.currentResponseId) return;
      const results = [...pendingResults];
      pendingResults.length = 0;
      this.#sendBeaconEvent('impression', {
        responseId: this.currentResponseId,
        results,
        banners: []
      });
    };

    this.impressionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const card = entry.target;
          const uid = card.getAttribute('data-content-uid');
          const impressionKey = card.getAttribute('data-impression-key');

          if (!uid || !impressionKey || this.trackedImpressions.has(impressionKey)) return;

          this.trackedImpressions.add(impressionKey);
          this.impressionObserver.unobserve(card);

          pendingResults.push({
            type: 'product',
            parentId: uid,
            uid
          });

          if (!flushScheduled) {
            flushScheduled = true;
            requestAnimationFrame(() => {
              flushScheduled = false;
              flush();
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    this.articlesContainer.querySelectorAll('li[data-content-uid]').forEach((card) => {
      const impressionKey = card.getAttribute('data-impression-key');
      if (impressionKey && !this.trackedImpressions.has(impressionKey)) {
        this.impressionObserver.observe(card);
      }
    });
  }

  #handleContentClick(uid) {
    if (!this.currentResponseId || !uid) return;

    this.#sendBeaconEvent('clickthrough', {
      responseId: this.currentResponseId,
      results: [{
        type: 'product',
        parentId: uid,
        uid
      }]
    }, true);
  }

  #getOrSetCookie(name) {
    let value = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${name}=`))
      ?.split('=')[1];
    if (!value) {
      value = crypto.randomUUID?.() || Math.random().toString(36).substring(2);
      document.cookie = `${name}=${value}; path=/; max-age=31536000`;
    }
    return value;
  }

  #trackContentView(data) {
    if (!data?.results?.length) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'view_search_content',
      search_term: this.currentSearchQuery,
      content_count: data.pagination?.totalResults || 0
    });
  }
}

if (!customElements.get('searchspring-content-listing')) {
  customElements.define('searchspring-content-listing', SearchspringContentListing);
}
